import React, { useRef } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { InfoTooltip } from './Infotooltip';
import { CardExportMenu } from './export/CardExportMenu';
import { fmtViewerTime } from './export/exportUtils';

// Viewer's browser timezone (same one the dashboard sends as ?tz=).
const TZ = (typeof Intl !== 'undefined' && Intl.DateTimeFormat().resolvedOptions().timeZone) || 'Europe/Amsterdam';

function ChartTooltipContent({ active, payload, label, t }) {
  if (active && payload && payload.length) {
    return (
      <div className="rounded-lg border bg-background p-2 shadow-sm">
        <div className="grid grid-cols-2 gap-2">
          <div className="flex flex-col">
            <span className="text-[0.70rem] uppercase text-muted-foreground">
              {payload[0].payload.period === 'day' ? t('time') : t('date')}
            </span>
            <span className="font-bold text-muted-foreground">{label}</span>
          </div>
          <div className="flex flex-col">
            <span className="text-[0.70rem] uppercase text-muted-foreground">Rate</span>
            <span className="font-bold">{payload[0].value.toFixed(1)}%</span>
          </div>
        </div>
      </div>
    );
  }
  return null;
}

// Placeholder wave: many peaks/dips across the full width, like the real
// trend line. Loading state only, never real data.
// y-values: lower number = taller peak, 38 = baseline.
const SKELETON_LINE =
  "M0,38 C2,38 3,25 6,25 L10,25 C11,13 16,12 18,22 C19,28 21,28 22,20 " +
  "C23,14 26,14 27,38 L30,38 C31,14 34,14 35,38 L37,38 C38,22 41,18 44,16 " +
  "C46,14 49,12 51,22 C52,28 53,38 54,38 L56,38 C57,26 59,24 63,24 " +
  "C66,24 67,22 70,28 C72,32 73,38 75,38 C77,38 77,20 80,18 " +
  "C83,16 84,22 86,28 C87,32 87,20 89,16 C91,12 93,12 94,28 C95,36 96,38 97,38 " +
  "C98,38 99,26 100,24";
const SKELETON_PATH_STROKE = SKELETON_LINE;
const SKELETON_PATH_FILL = `${SKELETON_LINE} L100,40 L0,40 Z`;

export function RecyclingChart({ data, loading = false, isCustomRange = false, bins = [] }) {
  const { t, i18n } = useTranslation();
  const cardRef = useRef(null);

  const chartData = data?.historical_data?.map(item => ({
    date: item.date,
    recyclingRate: item.rate,
    period: data.period
  })) || [];

  const currentRate = data?.recycling_rate || 0;
  const percentage  = currentRate.toFixed(1);
  const period      = data?.period || 'year';

  const getColorScheme = (rate) => {
    if (rate >= 70) return { stroke: '#10b981', fill: '#10b981', fillOpacity: 0.2, text: 'text-green-600' };
    if (rate >= 50) return { stroke: '#3b82f6', fill: '#3b82f6', fillOpacity: 0.2, text: 'text-blue-500' };
    return               { stroke: '#ef4444', fill: '#ef4444', fillOpacity: 0.2, text: 'text-red-500' };
  };

  const colorScheme = getColorScheme(currentRate);

  // Short ranges (week, 3 or 6 months, short custom ranges) show every label.
  // Longer ones (month, year, long custom ranges) let Recharts skip some so
  // the labels don't pile up.
  const showEveryLabel = chartData.length <= 7;

  // A calendar selection can come back from the backend with an hourly/daily
  // period (e.g. "day") rather than "custom...", so rely on the dashboard's
  // own flag as well as the period string.
  const isCustom = isCustomRange || period?.startsWith('custom');

  const getRangeLabel = () => {
    if (!data?.start_date || !data?.end_date) return null;
    // The backend computes the date boundaries in the viewer's timezone
    // (?tz=), so format with that same timezone here.
    const fmt = (iso) => new Date(iso).toLocaleDateString(i18n.language, { day: 'numeric', month: 'short', year: 'numeric', timeZone: TZ });
    if (isCustom) {
      const opts = { timeZone: TZ };
      const start = new Date(data.start_date);
      const end   = new Date(data.end_date);
      const sameYear =
        start.toLocaleDateString('en', { year: 'numeric', ...opts }) ===
        end.toLocaleDateString('en',   { year: 'numeric', ...opts });
      // Same year: "Oct 2 – Oct 3, 2026". Spanning years: "Dec 30, 2025 – Jan 2, 2026".
      const startLabel = start.toLocaleDateString(i18n.language, {
        day: 'numeric', month: 'short', ...(sameYear ? {} : { year: 'numeric' }), ...opts,
      });
      return `${startLabel} – ${fmt(data.end_date)}`;
    }
    if (period === 'day')   return fmt(data.start_date);
    if (period === 'month') return new Date(data.start_date).toLocaleDateString(i18n.language, { month: 'long', year: 'numeric', timeZone: TZ });
    if (period === 'year')  return new Date(data.start_date).toLocaleDateString(i18n.language, { year: 'numeric', timeZone: TZ });
    return `${fmt(data.start_date)} – ${fmt(data.end_date)}`;
  };

  // ── Export (card menu + page export both read this) ──
  const exportTitle = t('recyclingRate.defaultTitle');
  const exportMeta = {
    period: data?.start_date && data?.end_date
      ? `${period} (${fmtViewerTime(data.start_date, { seconds: false })} to ${fmtViewerTime(data.end_date, { seconds: false })})`
      : period,
    bins,
  };
  const getExportData = () => ({
    title: exportTitle,
    headers: ['Period', 'Recycling rate (%)'],
    rows: [
      ['Overall', currentRate.toFixed(1)],
      ...chartData.map(d => [d.date, Number(d.recyclingRate ?? 0).toFixed(1)]),
    ],
  });

  if (loading) {
    // Skeleton — header stays real, percentage becomes a bar, the chart area
    // gets a busy wavy line (many peaks/dips) and a row of placeholder bars
    // where the x-axis labels will be, so it reads as "a chart is coming".
    return (
      <div className="rounded-lg border bg-card text-card-foreground dark:hover:bg-slate-900 flex flex-col w-full h-[200px] motion-safe:animate-pulse" aria-busy="true">
        <span className="sr-only" role="status">{t('recyclingRate.loading', 'Loading…')}</span>
        <div className="px-4 py-3 flex items-center justify-between">
          <h2 className="tracking-tight text-sm font-medium pb-2">{t('recyclingRate.defaultTitle')}</h2>
          <div className="flex items-center gap-2 pb-2">
            <InfoTooltip text={t('recyclingRate.infoTooltip', 'Your waste that was correctly sorted into PMD, organic or paper.')} />
          </div>
        </div>

        <div className="px-4 pb-1">
          <div className="h-6 w-16 rounded bg-gray-200 dark:bg-slate-700" />
        </div>

        <div className="flex-1 w-full px-0 pt-0 overflow-hidden">
          <svg viewBox="0 0 100 40" preserveAspectRatio="none" className="w-full h-full" aria-hidden="true">
            <path d={SKELETON_PATH_FILL} className="fill-gray-200 dark:fill-slate-700" />
            <path d={SKELETON_PATH_STROKE} fill="none" className="stroke-gray-300 dark:stroke-slate-600" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
          </svg>
        </div>

        {/* placeholder x-axis labels */}
        <div className="flex justify-between px-5 pt-1 pb-3" aria-hidden="true">
          {Array.from({ length: 10 }).map((_, i) => (
            <div key={i} className="h-2 w-6 rounded bg-gray-200 dark:bg-slate-700" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div
      ref={cardRef}
      className="exportable-section rounded-lg border bg-card text-card-foreground dark:hover:bg-slate-900 hover:cursor-pointer flex flex-col w-full h-[200px]"
      data-export-title={exportTitle}
      data-export-data={JSON.stringify(getExportData())}
    >
      <div className="px-4 py-3 flex items-center justify-between">
        <h2 className="tracking-tight text-sm font-medium pb-2">{t('recyclingRate.defaultTitle')}</h2>
        <div className="flex items-center gap-2 pb-2">
          {isCustom && getRangeLabel() && (
            <span className="text-xs text-muted-foreground">{getRangeLabel()}</span>
          )}
          <CardExportMenu compact cardRef={cardRef} title={exportTitle} meta={exportMeta} getData={getExportData} />
          <InfoTooltip text={t('recyclingRate.infoTooltip', 'Your waste that was correctly sorted into PMD, organic or paper.')} />
        </div>
      </div>

      <div className={`px-4 pb-1 text-2xl font-bold ${colorScheme.text}`}>
        {percentage}%
      </div>

      <div className="flex-1 w-full px-0 pt-0 overflow-hidden rounded-b-lg">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 10, right: 20, left: 20, bottom: 20 }}>
            {/* Labels come straight from the backend ("14:00", "Sun 27",
                "Sep 04", "Oct 2026"). Short ranges show every label; longer
                ones keep the first and last and let Recharts thin the rest. */}
            <XAxis
              dataKey="date"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 10, fill: '#9CA3AF' }}
              dy={10}
              interval={showEveryLabel ? 0 : 'preserveStartEnd'}
            />
            <YAxis hide domain={[0, 100]} />
            <Tooltip content={<ChartTooltipContent t={t} />} wrapperStyle={{ outline: 'none' }} />
            <Area
              type="monotone"
              dataKey="recyclingRate"
              stroke={colorScheme.stroke}
              strokeWidth={3}
              fill={colorScheme.fill}
              fillOpacity={colorScheme.fillOpacity}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}